import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { moodAPI, journalAPI, sessionAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import { 
  Smile, Meh, Frown, Sun, Cloud, ArrowLeft, Plus, 
  TrendingUp, TrendingDown, Minus, Calendar, BookOpen, 
  FileText, Trash2, Sparkles 
} from 'lucide-react';

const MOOD_OPTIONS = [
  { level: 5, label: 'Great', icon: Smile, color: 'text-emerald-500', bg: 'bg-emerald-500' },
  { level: 4, label: 'Good', icon: Sun, color: 'text-sage-400', bg: 'bg-sage-400' },
  { level: 3, label: 'Okay', icon: Meh, color: 'text-sand-500', bg: 'bg-sand-500' },
  { level: 2, label: 'Low', icon: Cloud, color: 'text-slate-400', bg: 'bg-slate-400' },
  { level: 1, label: 'Difficult', icon: Frown, color: 'text-terracotta-400', bg: 'bg-terracotta-400' },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [moodEntries, setMoodEntries] = useState([]);
  const [moodStats, setMoodStats] = useState({ average_mood: 0, entries_count: 0, trend: 'neutral' });
  const [journalEntries, setJournalEntries] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodNotes, setMoodNotes] = useState('');
  const [journalTitle, setJournalTitle] = useState('');
  const [journalContent, setJournalContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [moods, stats, journals, sums] = await Promise.all([
        moodAPI.getAll(30),
        moodAPI.getStats(7),
        journalAPI.getAll(20),
        sessionAPI.getSummaries()
      ]);
      setMoodEntries(moods.data);
      setMoodStats(stats.data);
      setJournalEntries(journals.data);
      setSummaries(sums.data.summaries || []);
    } catch (error) {
      console.error('Load data error:', error);
    }
  };

  const saveMoodEntry = async () => {
    if (!selectedMood) return;
    setIsLoading(true);
    try {
      const mood = MOOD_OPTIONS.find(m => m.level === selectedMood);
      await moodAPI.create({
        mood_level: selectedMood,
        mood_label: mood.label,
        notes: moodNotes || null
      });
      setSelectedMood(null);
      setMoodNotes('');
      loadData();
    } catch (error) {
      console.error('Save mood error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveJournalEntry = async () => {
    if (!journalTitle.trim() || !journalContent.trim()) return;
    setIsLoading(true);
    try {
      await journalAPI.create(journalTitle, journalContent);
      setJournalTitle('');
      setJournalContent('');
      loadData();
    } catch (error) {
      console.error('Save journal error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteJournalEntry = async (id) => {
    try {
      await journalAPI.delete(id);
      loadData();
    } catch (error) {
      console.error('Delete journal error:', error);
    }
  };

  const chartData = moodEntries.slice(0, 14).reverse().map((entry, index) => ({
    day: new Date(entry.timestamp).toLocaleDateString('en-US', { weekday: 'short' }),
    mood: entry.mood_level,
    label: entry.mood_label
  }));

  const TrendIcon = moodStats.trend === 'improving' ? TrendingUp : 
                    moodStats.trend === 'declining' ? TrendingDown : Minus;
  const trendColor = moodStats.trend === 'improving' ? 'text-emerald-500' :
                     moodStats.trend === 'declining' ? 'text-terracotta-400' : 'text-muted-foreground';

  return (
    <div className="min-h-screen bg-background grain">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="container mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/chat')} className="rounded-full h-9 w-9 sm:h-10 sm:w-10" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <h1 className="text-lg sm:text-xl font-heading font-bold">Mood Dashboard</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6">
          <Card className="shadow-card border-0">
            <CardContent className="p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Avg Mood</p>
                  <p className="text-xl sm:text-3xl font-bold text-foreground">{moodStats.average_mood.toFixed(1)}</p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card border-0">
            <CardContent className="p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Check-ins</p>
                  <p className="text-xl sm:text-3xl font-bold text-foreground">{moodStats.entries_count}</p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-accent/10 flex items-center justify-center">
                  <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-accent" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card border-0">
            <CardContent className="p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Trend</p>
                  <p className={`text-sm sm:text-lg font-semibold capitalize ${trendColor}`}>{moodStats.trend}</p>
                </div>
                <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center ${
                  moodStats.trend === 'improving' ? 'bg-emerald-100' :
                  moodStats.trend === 'declining' ? 'bg-terracotta-100' : 'bg-muted'
                }`}>
                  <TrendIcon className={`w-4 h-4 sm:w-6 sm:h-6 ${trendColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mood Chart */}
        {chartData.length > 0 && (
          <Card className="shadow-card border-0">
            <CardHeader className="px-4 sm:px-6 pb-2">
              <CardTitle className="text-base sm:text-lg">Mood Trends</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Your emotional journey over the past week</CardDescription>
            </CardHeader>
            <CardContent className="px-2 sm:px-6">
              <div className="h-48 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(155, 14%, 55%)" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(155, 14%, 55%)" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(30, 10%, 90%)" />
                    <XAxis dataKey="day" stroke="hsl(210, 5%, 45%)" fontSize={10} />
                    <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} stroke="hsl(210, 5%, 45%)" fontSize={10} width={25} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(0, 0%, 100%)', 
                        border: 'none', 
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px -2px rgba(124, 154, 146, 0.1)',
                        fontSize: '12px'
                      }}
                      formatter={(value, name) => [MOOD_OPTIONS.find(m => m.level === value)?.label || value, 'Mood']}
                    />
                    <Area type="monotone" dataKey="mood" stroke="hsl(155, 14%, 55%)" fill="url(#moodGradient)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs for Check-in, Journal, Summaries */}
        <Tabs defaultValue="checkin" className="space-y-4 sm:space-y-6">
          <TabsList className="bg-muted/50 p-1 rounded-xl sm:rounded-2xl w-full grid grid-cols-3">
            <TabsTrigger value="checkin" className="rounded-lg sm:rounded-xl text-xs sm:text-sm px-2" data-testid="tab-checkin">Check-in</TabsTrigger>
            <TabsTrigger value="journal" className="rounded-lg sm:rounded-xl text-xs sm:text-sm px-2" data-testid="tab-journal">Journal</TabsTrigger>
            <TabsTrigger value="summaries" className="rounded-lg sm:rounded-xl text-xs sm:text-sm px-2" data-testid="tab-summaries">Summaries</TabsTrigger>
          </TabsList>

          {/* Mood Check-in */}
          <TabsContent value="checkin">
            <Card className="shadow-card border-0">
              <CardHeader className="px-4 sm:px-6 pb-2">
                <CardTitle className="text-base sm:text-lg">How are you feeling right now?</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Take a moment to check in with yourself</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
                <div className="flex justify-center gap-2 sm:gap-4 flex-wrap">
                  {MOOD_OPTIONS.map((mood) => (
                    <button
                      key={mood.level}
                      onClick={() => setSelectedMood(mood.level)}
                      className={`flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 ${
                        selectedMood === mood.level
                          ? 'bg-primary/10 ring-2 ring-primary scale-105 sm:scale-110'
                          : 'bg-muted/50 hover:bg-muted'
                      }`}
                      data-testid={`mood-select-${mood.level}`}
                    >
                      <mood.icon className={`w-7 h-7 sm:w-10 sm:h-10 ${mood.color}`} />
                      <span className="text-xs sm:text-sm font-medium">{mood.label}</span>
                    </button>
                  ))}
                </div>

                {selectedMood && (
                  <div className="space-y-3 sm:space-y-4 animate-fade-in">
                    <Textarea
                      placeholder="Any notes about how you're feeling? (optional)"
                      value={moodNotes}
                      onChange={(e) => setMoodNotes(e.target.value)}
                      className="bg-input border-0 rounded-xl min-h-20 sm:min-h-24 resize-none text-sm"
                      data-testid="mood-notes"
                    />
                    <Button
                      onClick={saveMoodEntry}
                      disabled={isLoading}
                      className="w-full rounded-full btn-hover text-sm"
                      data-testid="save-mood-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Save Check-in
                    </Button>
                  </div>
                )}

                {/* Recent Entries */}
                {moodEntries.length > 0 && (
                  <div className="space-y-2 sm:space-y-3 pt-4 sm:pt-6 border-t border-border">
                    <h4 className="font-semibold text-xs sm:text-sm text-muted-foreground">Recent Check-ins</h4>
                    {moodEntries.slice(0, 5).map((entry) => {
                      const mood = MOOD_OPTIONS.find(m => m.level === entry.mood_level);
                      return (
                        <div key={entry.id} className="flex items-center gap-3 sm:gap-4 p-2 sm:p-3 rounded-xl bg-muted/30">
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center ${mood?.bg}/20`}>
                            {mood && <mood.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${mood.color}`} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs sm:text-sm">{entry.mood_label}</div>
                            {entry.notes && <div className="text-xs text-muted-foreground truncate">{entry.notes}</div>}
                          </div>
                          <div className="text-xs text-muted-foreground flex-shrink-0">
                            {new Date(entry.timestamp).toLocaleDateString()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Journal */}
          <TabsContent value="journal">
            <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
              <Card className="shadow-card border-0">
                <CardHeader className="px-4 sm:px-6 pb-2">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
                    New Entry
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4 px-4 sm:px-6">
                  <Input
                    placeholder="Entry title..."
                    value={journalTitle}
                    onChange={(e) => setJournalTitle(e.target.value)}
                    className="bg-input border-0 rounded-xl h-10 sm:h-12 text-sm"
                    data-testid="journal-title"
                  />
                  <Textarea
                    placeholder="Write your thoughts..."
                    value={journalContent}
                    onChange={(e) => setJournalContent(e.target.value)}
                    className="bg-input border-0 rounded-xl min-h-32 sm:min-h-40 resize-none text-sm"
                    data-testid="journal-content"
                  />
                  <Button
                    onClick={saveJournalEntry}
                    disabled={!journalTitle.trim() || !journalContent.trim() || isLoading}
                    className="w-full rounded-full btn-hover text-sm"
                    data-testid="save-journal-btn"
                  >
                    Save Entry
                  </Button>
                </CardContent>
              </Card>

              <Card className="shadow-card border-0">
                <CardHeader className="px-4 sm:px-6 pb-2">
                  <CardTitle className="text-base sm:text-lg">Recent Entries</CardTitle>
                </CardHeader>
                <CardContent className="px-4 sm:px-6">
                  <ScrollArea className="h-64 sm:h-80">
                    <div className="space-y-2 sm:space-y-3 pr-2 sm:pr-4">
                      {journalEntries.length === 0 ? (
                        <p className="text-center text-muted-foreground py-6 sm:py-8 text-sm">No journal entries yet</p>
                      ) : (
                        journalEntries.map((entry) => (
                          <div key={entry.id} className="p-3 sm:p-4 rounded-xl bg-muted/30 group">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-xs sm:text-sm truncate">{entry.title}</h4>
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{entry.content}</p>
                                <p className="text-xs text-muted-foreground mt-1 sm:mt-2">
                                  {new Date(entry.timestamp).toLocaleDateString()}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-7 h-7 sm:w-8 sm:h-8 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 flex-shrink-0"
                                onClick={() => deleteJournalEntry(entry.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Session Summaries */}
          <TabsContent value="summaries">
            <Card className="shadow-card border-0">
              <CardHeader className="px-4 sm:px-6 pb-2">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                  Session Summaries
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">AI-generated insights from your conversations</CardDescription>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                {summaries.length === 0 ? (
                  <div className="text-center py-8 sm:py-12 text-muted-foreground">
                    <FileText className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 opacity-30" />
                    <p className="text-xs sm:text-sm">No summaries yet. Chat more to generate insights!</p>
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {summaries.map((summary) => (
                      <div key={summary.id} className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-muted/30 space-y-3 sm:space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-xs sm:text-sm font-medium text-foreground">
                            {new Date(summary.timestamp).toLocaleDateString('en-US', { 
                              weekday: 'short', month: 'short', day: 'numeric' 
                            })}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm text-foreground">{summary.summary}</p>
                        
                        {summary.emotions_detected?.length > 0 && (
                          <div>
                            <span className="text-xs font-medium text-muted-foreground">Emotions:</span>
                            <div className="flex gap-1.5 sm:gap-2 mt-1 flex-wrap">
                              {summary.emotions_detected.map((emotion, i) => (
                                <span key={i} className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-primary/10 text-primary text-xs">
                                  {emotion}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {summary.grounding_exercises?.length > 0 && (
                          <div>
                            <span className="text-xs font-medium text-muted-foreground">Suggested Exercises:</span>
                            <ul className="mt-1 space-y-0.5 sm:space-y-1">
                              {summary.grounding_exercises.map((exercise, i) => (
                                <li key={i} className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2">
                                  <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-accent flex-shrink-0" />
                                  {exercise}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Dashboard;
